import { trackUsageAction } from 'lib/usageAnalytics';
import React, {
  Dispatch, SetStateAction, useState, useEffect, useContext,
} from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import ExportIcon from '@images/IconHeader/export.svg';
import { format as dateFormat } from 'date-fns';
import { MeType } from 'models/me';
import { withMe } from 'components/MeUserWrapper';
import { RolesTypes } from '../../types/acl';
import { UserContext } from '../ACLWrapper';
import HeaderPopover from '../layout/HeaderPopover';
import { CREATE_OUTCOMING_FILE, EXPORT_DATETIME_FORMAT } from '../../mutation/export';
import { SNACKBAR_TIMEOUT } from '../constants';

export type DialogExportProps = {
  setDialogInfo: Dispatch<SetStateAction<{ start: boolean; available: boolean; data?: object; format: string; }>>,
  objectId?: string,
  templateId?: string,
  teamId?: string,
}

type ExportDataType = {
  format: 'pdf' | 'csv' | 'xlsx' | 'xml' | 'txt' | 'raw',
  defaultLabel: string,
  label?: string,
  data?: object,
  dialogFormat?: 'standard' | 'rwp' | 'split' | 'events' | 'new',
  DialogComponent?: React.FC<DialogExportProps>,
  downloadUrl?: string,
  storedOnS3?: boolean,
  key?: string,
  customTarget?: string,
  permission?: string,
}

type PropsIn = {
  teamId: string,
  templateId?: string,
  disable?: boolean,
  target: 'AthleteSession' | 'AthleteSessionList' | 'TeamSession' | 'IncomingFile' | 'Track' | 'ASProfile' | 'MPProfile'
    | 'ASProfileList' | 'MPProfileList' | 'ASProfileComparison' | 'MPProfileComparison' | 'Trend' | 'Summary' | 'Radar' | 'Scatter' | 'RPEList',
  exportData: ExportDataType[],
  filename?: string,
  enableDialog?: boolean,
  collapseOnSmallDesktop?: boolean,
  collapseOnMobile?: boolean,
  disabledExportMenuItems?: object,
  buttonSize?: 'sm' | 'md' | 'lg',
  tooltipText?:string,
  kpiList?: string[],
  section?: keyof RolesTypes,
}

export const ExportButton = withMe((props: PropsIn & {me: MeType}) => {
  const {
    teamId,
    target,
    exportData,
    disable,
    filename,
    enableDialog,
    collapseOnSmallDesktop,
    collapseOnMobile,
    disabledExportMenuItems,
    buttonSize,
    tooltipText,
    templateId: templateIdProp,
    kpiList,
    section,
    me,
  } = props;

  const templateId = templateIdProp || me.template?.id;

  const { t } = useTranslation();
  const { userCan } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [dialogInfo, setDialogInfo] = useState({
    start: false,
    available: false,
    format: '',
  });

  const [dialogType, setDialogType] = useState('');

  const handleDialogAction = (format: ExportDataType['format'], data: object, dialog: ExportDataType['dialogFormat']) => {
    const sessionDialog = target === 'TeamSession' && (['rwp', 'split'].includes(dialog) || enableDialog);
    if (sessionDialog || enableDialog) {
      setDialogInfo({
        start: false,
        available: true,
        format,
        data,
      });
      setDialogType(dialog);
    } else {
      doExport(format, data, dialog);
    }
  };

  const [createExport] = useMutation(CREATE_OUTCOMING_FILE, {
    onCompleted: (data) => {
      enqueueSnackbar(`${t('exports.creating', 'creating')} ${data.res.filename}`, {
        variant: 'success',
        autoHideDuration: 2000,
      });
      if (dialogInfo.available) {
        setDialogInfo((prev) => ({
          ...prev,
          available: false,
          start: false,
        }));
      }
    },
    onError: () => {
      enqueueSnackbar(t('exports.dialog.exportError', 'error during request for document'), {
        variant: 'error',
        autoHideDuration: SNACKBAR_TIMEOUT,
      });
      if (dialogInfo.available) {
        setDialogInfo((prev) => ({
          ...prev,
          available: false,
          start: false,
        }));
      }
    },
  });

  const doExport = (exportFormat: ExportDataType['format'], data: object, label?: string) => {
    const teamSessionTarget = label === 'events' ? 'TeamSessionEvents'
      : label === 'rwp' ? 'TeamSessionRollingWindowPeaks'
        : label === 'split' ? 'TeamSessionSplitted' : target;

    const trackTarget = label === 'raw' ? 'Gpexe' : target;

    const ASProfileListTarget = label === 'extended' ? 'ASProfileExtList' : target;

    const formatFilename = (filename, label, target, exportFormat = 'csv') => {
      if (label === 'raw' && target === 'Track') {
        filename = filename.replace('gpexe_track', 'gpexe_raw_track');
      }
      if (['TeamSession', 'TeamSessionEvents', 'TeamSessionRollingWindowPeaks', 'TeamSessionSplitted'].includes(target)) {
        if (label !== undefined && label !== 'standard') {
          return `${filename}_${label}.${exportFormat}`;
        }
      }
      return `${filename}.${exportFormat}`;
    };

    trackUsageAction('export_requested', {
      format: exportFormat,
      target: target === 'TeamSession' ? teamSessionTarget
        : target === 'Track' ? trackTarget
          : target === 'ASProfileList' ? ASProfileListTarget : target,
      count: 1,
    }, () => createExport({
      variables: {
        filename: filename ? formatFilename(filename, label, teamSessionTarget, exportFormat)
          : `${target}_${dateFormat(new Date(), EXPORT_DATETIME_FORMAT)}.${exportFormat}`,
        target: target === 'TeamSession' ? teamSessionTarget : target === 'Track' ? trackTarget : target === 'ASProfileList' ? ASProfileListTarget : target,
        teamId,
        templateId,
        format: exportFormat,
        data: JSON.stringify(data),
      },
    }), true);
  };

  useEffect(() => {
    if (dialogInfo.start) {
      doExport(dialogInfo.format, dialogInfo.data, dialogType);
    }
  }, [dialogInfo.start]);

  const visibleExportData = exportData.filter(
    (exp) => !section || !exp.permission || userCan({ section, type: 'exports', permission: exp.permission }),
  );

  if (visibleExportData.length === 0) return null;

  return (
    <>
      <HeaderPopover
        buttonId="export-button"
        className="export-button"
        key="export-button"
        icon={<ExportIcon />}
        tooltip={tooltipText}
        disabled={disable}
        label={t('header.action.export', 'export')}
        collapseOnSmallDesktop={collapseOnSmallDesktop}
        collapseOnMobile={collapseOnMobile}
        disabledExportMenuItems={disabledExportMenuItems}
        popoverContent={visibleExportData.map((exp) => ({
          key: exp.format,
          value: exp.format,
          label: exp.label || t(`exports.type.${exp.format}`, `${exp.format}`).toUpperCase(),
          onClick:
           (enableDialog || target === 'TeamSession') && exp.dialogFormat
             ? () => handleDialogAction(exp.format, exp.data, exp.dialogFormat)
             : () => doExport(exp.format, exp.data, exp?.defaultLabel?.split(' ')[0]),
        }))}
        buttonSize={buttonSize}
      />
      { dialogInfo.available
        && visibleExportData.map(({
          DialogComponent, dialogFormat, data, format,
        }) => {
          if (DialogComponent && dialogFormat === dialogType && format === dialogInfo.format) {
            return (
              <DialogComponent
                key={data?.id}
                teamId={teamId}
                setDialogInfo={setDialogInfo}
                objectId={data?.id}
                templateId={templateId}
              />
            );
          }
        })}
    </>
  );
});
